import { z } from "zod";

export const UserId = z.uuid().brand<"UserId">().meta({
  id: "UserId",
  description:
    "The id of a Pax user. Better Auth mints it at signup, it names the same person for the life of the account, and it is never handed to anybody else.",
  example: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
});
export type UserId = z.infer<typeof UserId>;

export const GameId = z.uuid().brand<"GameId">().meta({
  id: "GameId",
  description: "A client-generated game id.",
  example: "9d2f6c1e-3b7a-4f0e-8c21-5a6d7e8f9a0b",
});
export type GameId = z.infer<typeof GameId>;

export const GameKind = z.enum(["play", "eval"]).meta({
  id: "GameKind",
  description:
    "`play`: a game people play. `eval`: a game an evaluation plays, whose telemetry is kept for good. Fixed when the game is created.",
  example: "play",
});
export type GameKind = z.infer<typeof GameKind>;

export const PresetId = z.uuid().brand<"PresetId">().meta({
  id: "PresetId",
  description: "The stable id of a preset.",
  example: "3b58d323-cbb8-57dd-ae77-1a9974b5afa5",
});
export type PresetId = z.infer<typeof PresetId>;

export const PresetVersionId = z.uuid().brand<"PresetVersionId">().meta({
  id: "PresetVersionId",
  description: "The immutable id of a published preset version.",
  example: "f176c8e6-c8ed-5d9b-85b4-3c176df9595f",
});
export type PresetVersionId = z.infer<typeof PresetVersionId>;

export const DraftId = z.uuid().brand<"DraftId">().meta({
  id: "DraftId",
  description: "The id of a draft, which the version it's published as keeps.",
  example: "0199f0c4-1b3a-7e2d-9a41-6c0d8f2b7e15",
});
export type DraftId = z.infer<typeof DraftId>;

export const AgentId = z.uuid().brand<"AgentId">().meta({
  id: "AgentId",
  description: "The stable id of an agent.",
  example: "3b58d323-cbb8-57dd-ae77-1a9974b5afa5",
});
export type AgentId = z.infer<typeof AgentId>;

export const AgentVersionId = z.uuid().brand<"AgentVersionId">().meta({
  id: "AgentVersionId",
  description: "The immutable id of a published agent version.",
  example: "f176c8e6-c8ed-5d9b-85b4-3c176df9595f",
});
export type AgentVersionId = z.infer<typeof AgentVersionId>;

export const PaginationQuery = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().min(1).max(512).optional().meta({
    description:
      "Opaque position from a previous page's `nextCursor`. Omit for the first page and pass it back unchanged.",
  }),
});
export type PaginationQuery = z.infer<typeof PaginationQuery>;

export const Timestamp = z.iso.datetime({ offset: true }).meta({
  id: "Timestamp",
  description: "An ISO 8601 timestamp with an explicit UTC offset.",
  example: "2026-09-04T17:00:00.000Z",
});
export type Timestamp = z.infer<typeof Timestamp>;
